import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { of } from 'rxjs';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';

import { I18nService, untilDestroyed, UrlParamsService } from '@app/core';
import { ActionEnum, AuthenticationMethod, DeliveryMethodEnum, FontEnum, LanguageEnum, PositionElementEnum, SelectOption, SignatureFormat, Signer, User, UserSignatureInput } from '@app/models';
import { AuthenticationService, DateService, EnterpriseService, ErrorHandlerService, NotyService, OAuthService, UserService } from '@app/services';
import { SignaturePadComponent } from '@app/shared/signature-pad/signature-pad.component';
import { EditProfileModalService } from './edit-profile-modal/edit-profile-modal.service';
import { EditSignatureModalService } from './edit-signature-modal/edit-signature-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { EditLocationModalService } from '@app/pages/configurations/configurations-profile/edit-location-modal/edit-location-modal.service';
import { PreLoginModalService } from '@app/pages/documents/documents-show/pre-login-modal/pre-login-modal.service';
import { ConfigurationsStandaloneIndexComponent } from '../configurations-standalone-index/configurations-standalone-index.component';
import { AuthLoginModalService } from '@app/pages/documents/documents-show/auth-login-modal/auth-login-modal.service';

import { InitialsNamePipe } from '@app/shared/pipes/initials-name.pipe';
import { SignatureNamePipe } from '@app/shared/pipes/signature-name.pipe';

@Component({
  selector: 'app-configurations-profile',
  templateUrl: './configurations-profile.component.html',
  styleUrls: ['../configurations.scss', './configurations-profile.component.scss'],
  standalone: false
})
export class ConfigurationsProfileComponent implements OnInit, OnDestroy {
  signatureNamePipe = new SignatureNamePipe();
  initialsPipe = new InitialsNamePipe();
  signatureForm: UntypedFormGroup;
  currentUser: User;
  fakeSigner: Signer;
  currentCountryIcon: string;
  isLoading = false;
  isInitialUserLoading = true;
  isEditingSignature = false;
  canSubmitForm = true;
  isMobile = false;

  readonly standalone: boolean;
  readonly targetUser: Record<string, string> = {};
  readonly countries: SelectOption[] = [];
  readonly languages: SelectOption[] = [];
  readonly timezones: SelectOption[] = [];
  readonly dateFormatOptions: SelectOption[] = [];
  readonly SignatureFormat = SignatureFormat;
  readonly PositionElementEnum = PositionElementEnum;

  @ViewChild('signaturePad', { static: false }) private signaturePad: SignaturePadComponent;

  constructor(
    public dateService: DateService,
    public i18nService: I18nService,
    public urlParamsService: UrlParamsService,
    public enterpriseService: EnterpriseService,
    private route: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private errorHandlerService: ErrorHandlerService,
    private notyService: NotyService,
    private editProfileModalService: EditProfileModalService,
    private editSignatureModalService: EditSignatureModalService,
    private translateService: TranslateService,
    private editLocationModalService: EditLocationModalService,
    private preLoginModalService: PreLoginModalService,
    private authLoginModalService: AuthLoginModalService,
    private oauthService: OAuthService
  ) {
    this.signatureForm = this.formBuilder.group({
      signature: this.formBuilder.group({
        font: FontEnum.Autograf,
        format: SignatureFormat.Handwriting,
        text: '',
        file_image: null,
        file_draw: null
      }),
      initials: this.formBuilder.group({
        font: FontEnum.Autograf,
        format: SignatureFormat.Handwriting,
        text: '',
        file_image: null,
        file_draw: null
      })
    });

    this.languages = this.i18nService.supportedLanguagesAsSelectOptions || [];
    this.countries = this.i18nService.countriesWithCodes || [];
    this.timezones = this.i18nService.timezonesWithGMT || [];
    this.dateFormatOptions = this.i18nService.dateFormatOptions || [];

    this.standalone = this.route.parent.routeConfig?.component?.name === ConfigurationsStandaloneIndexComponent.name;
    this.targetUser.email = this.route.snapshot.queryParams.email;
    this.targetUser.phone = this.route.snapshot.queryParams.phone ? this.route.snapshot.queryParams.phone.replace(/[^0-9]/g, '') : null;
  }

  ngOnInit() {
    const tmpUser = this.userService.getTmpUser();
    tmpUser.email = this.route.snapshot.queryParams.email || tmpUser.email;
    tmpUser.phone = this.route.snapshot.queryParams.phone || tmpUser.phone;
    this.setCurrentUser(tmpUser);

    if (!this.authenticationService.isAuthenticated()) {
      this.runLoginFlow();
    }

    this.userService
      .watchCurrentUser()
      .pipe(
        filter(user => !!user),
        untilDestroyed(this)
      )
      .subscribe({
        next: user => {
          this.isInitialUserLoading = false;
          this.setCurrentUser(user);
          this.runLoginFlow(user);

          if (user?.locale?.country) {
            const countryOption = this.countries.find(country => country.key === user.locale.country);
            this.currentCountryIcon = !!countryOption ? `${countryOption.icon} fis` : null;
          }
        },
        error: error => {
          this.isInitialUserLoading = false;
          this.errorHandlerService.handle(error);
        }
      });

    this.isMobile = window.innerWidth <= 992;
  }

  ngOnDestroy() {}

  openEditProfileModal() {
    this.editProfileModalService.open({ user: this.currentUser }).subscribe();
  }

  openEditSignatureModalForMobile() {
    this.editSignatureModalService.open({ user: this.currentUser }).subscribe({
      next: data => {
        this.updateSignature({ signature: data.signature, initials: data.initials });
      }
    });
  }

  openEditLocationModal() {
    this.editLocationModalService.open({ user: this.currentUser }).subscribe();
  }

  updateSignature(params?: { signature: UserSignatureInput; initials: UserSignatureInput }) {
    if (!this.canSubmitForm) {
      return;
    }

    this.signatureForm.markAllAsTouched();

    let signatureFile: File | null = null;
    let initialsFile: File | null = null;

    const signatureData = params?.signature || this.signatureForm.get('signature')?.value;
    const initialsData = params?.initials || this.signatureForm.get('initials')?.value;

    const signatureFormat: SignatureFormat = signatureData.format;
    const initialsFormat: SignatureFormat = signatureData.format;

    const isSignatureEmpty =
      (signatureFormat === SignatureFormat.Draw && !params?.signature && (this.signaturePad.isEmptySignature() || this.signaturePad.isMinSize('signature'))) ||
      (signatureFormat === SignatureFormat.Image && !params?.signature && !this.signaturePad.selectedSignatureImage && !this.signaturePad.selectedSignatureImageUrl) ||
      (signatureFormat === SignatureFormat.Handwriting && !signatureData.text?.trim());

    const isInitialsEmpty =
      (initialsFormat === SignatureFormat.Draw && !params?.initials && (this.signaturePad.isEmptyInitials() || this.signaturePad.isMinSize('initials'))) ||
      (initialsFormat === SignatureFormat.Image && !params?.initials && !this.signaturePad.selectedInitialsImage && !this.signaturePad.selectedInitialsImageUrl) ||
      (initialsFormat === SignatureFormat.Handwriting && !initialsData.text?.trim());

    if (isSignatureEmpty && isInitialsEmpty) {
      this.notyService.error(this.translateService.instant('notyService.cannotSendEmptySignature'));
      return;
    }

    const signatureParams: { user: UserSignatureInput } = { user: { ...signatureData } };
    const initialsParams: { user: UserSignatureInput } = { user: { ...initialsData } };

    if (signatureFormat === SignatureFormat.Draw) {
      signatureFile = this.signaturePad?.toImageFile('signature') || params?.signature?.file_draw;
      signatureParams.user.file_draw = signatureFile;
    } else if (signatureFormat === SignatureFormat.Image) {
      signatureFile = this.signaturePad?.toImageFile('signature') || params?.signature?.file_image;
      signatureParams.user.file_image = signatureFile;
    }

    if (initialsFormat === SignatureFormat.Draw) {
      initialsFile = this.signaturePad?.toImageFile('initials') || params?.initials?.file_draw;
      initialsParams.user.file_draw = initialsFile;
    } else if (initialsFormat === SignatureFormat.Image) {
      initialsFile = this.signaturePad?.toImageFile('initials') || params?.initials?.file_image;
      initialsParams.user.file_image = initialsFile;
    }

    [signatureParams, initialsParams].forEach(param => {
      if (!param.user.file_draw) delete param.user.file_draw;
      if (!param.user.file_image) delete param.user.file_image;
    });

    this.isLoading = true;

    const signatureRequest$ = isSignatureEmpty ? of(null) : this.userService.updateCurrentUserSignature(signatureParams);
    const initialsRequest$ = isInitialsEmpty ? of(null) : this.userService.updateCurrentUserInitials(initialsParams);

    signatureRequest$
      .pipe(
        switchMap(() => initialsRequest$),
        finalize(() => {
          this.isLoading = false;
          this.isEditingSignature = false;
          if (!params && this.signaturePad) {
            this.signaturePad.clear();
          }
        })
      )
      .subscribe({
        error: err => this.errorHandlerService.handle(err)
      });
  }

  get helpLink(): string {
    const lang = this.currentUser.locale.language === 'pt-BR' ? 'pt-BR' : 'en';
    return `https://ajuda.autentique.com.br/${lang}/articles/10394056`;
  }

  private runLoginFlow(currentUser?: User) {
    if (!this.standalone) {
      return;
    }
    if (this.authenticationService.isAuthenticated()) {
      if (!currentUser) {
        return;
      } else if (this.targetUser.email && this.targetUser.email === currentUser.email) {
        return;
      } else if (this.targetUser.phone && this.targetUser.phone === (currentUser.phone || '').replace(/[^0-9]/g, '')) {
        return;
      } else if (!this.targetUser.email && !this.targetUser.phone) {
        return;
      }
    }

    const login$ = of(null).pipe(
      tap(() => this.authenticationService.logout().subscribe()),
      switchMap(() => this.preLoginModalService.open({ blockDismiss: true })),
      switchMap(data =>
        data.option === AuthenticationMethod.Email
          ? this.authLoginModalService.open({ email: this.targetUser.email }).pipe(tap(user => (currentUser = user)))
          : this.oauthService.login(data.option, { ...(data.additionalInfo || {}), phoneNumber: this.targetUser?.phone }).pipe(
              switchMap(() => this.userService.getCurrentUser({ fetchPolicy: 'network-only' })),
              tap(user => (currentUser = user))
            )
      ),
      finalize(() => this.runLoginFlow(currentUser)) // Recursively makes sure the user is correctly logged in
    );

    login$.subscribe({
      error: error => this.errorHandlerService.handle(error)
    });
  }

  private setCurrentUser(user: User) {
    this.currentUser = user;
    this.signatureForm.get('signature')?.patchValue({
      font: this.currentUser.settings.font,
      format: this.currentUser.settings.format,
      text: this.currentUser.settings.signature_text || this.signatureNamePipe.transform(this.currentUser.name)
    });
    this.signatureForm.get('initials')?.patchValue({
      font: this.currentUser.settings.font,
      format: this.currentUser.settings.format,
      text: this.currentUser.settings.initials_text || this.initialsPipe.transform(this.currentUser.name)
    });
    this.setFakeSigner(this.currentUser);
  }

  private setFakeSigner(user: User) {
    this.fakeSigner = {
      public_id: '123',
      email: user.email,
      name: user.email ? null : user.name,
      parent: null,
      created_at: user.created_at,
      updated_at: user.created_at,
      archived_at: null,
      delivery_method: (user.phone && DeliveryMethodEnum.DeliveryMethodWhatsapp) || (user.email && DeliveryMethodEnum.DeliveryMethodEmail) || DeliveryMethodEnum.DeliveryMethodLink,
      action: { name: ActionEnum.Sign },
      email_events: { delivered: null, opened: null, reason: null, refused: null, sent: null },
      viewed: null,
      signed: {
        ip: '127.0.0.1',
        reason: null,
        created_at: new Date().toJSON(),
        geolocation: { country: 'Brazil', countryISO: 'BR', state: 'São Paulo', stateISO: 'SP', city: 'São Paulo', zipcode: '04026', latitude: '0', longitude: '0' }
      },
      rejected: null,
      positions: [],
      link: null,
      user: {
        id: user.id,
        name: user.name,
        email: user.email,
        cpf: user.cpf,
        birthday: user.birthday,
        company: user.company,
        phone: user.phone,
        escavador: null,
        organization: { id: user.organization.id || null, name: user.organization.name || null },
        settings: null
      }
    };
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.isMobile = window.innerWidth <= 992;
  }
}
